import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const RESULTS = [
  { value: "favorable",                 label: "Favorable" },
  { value: "favorable_with_conditions", label: "Favorable amb condicions" },
  { value: "unfavorable",               label: "Desfavorable" },
  { value: "pending",                   label: "Pendent" },
];

const TYPES = [
  { value: "initial",        label: "Inicial" },
  { value: "periodic",       label: "Periòdica" },
  { value: "after_incident", label: "Posterior a incident" },
  { value: "voluntary",      label: "Voluntària" },
];

const STATUSES = [
  { value: "scheduled",  label: "Programada" },
  { value: "completed",  label: "Realitzada" },
  { value: "overdue",    label: "Endarrerida" },
  { value: "cancelled",  label: "Cancel·lada" },
];

export default function InspectionForm({ inspection, installation, venueId, onSave, onClose }) {
  const [form, setForm] = useState({
    venue_id: venueId,
    installation_id: installation?.id || "",
    installation_name: installation?.name || "",
    inspection_date: new Date().toISOString().split("T")[0],
    next_inspection_date: "",
    inspection_type: "periodic",
    inspector_entity: "",
    inspector_name: "",
    result: "pending",
    deficiencies: [],
    observations: "",
    cost: "",
    status: "scheduled",
    notes: "",
    ...inspection,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addDeficiency = () => {
    set("deficiencies", [...(form.deficiencies || []), { code: "", description: "", severity: "lleu", resolved: false }]);
  };

  const removeDeficiency = (i) => {
    set("deficiencies", form.deficiencies.filter((_, idx) => idx !== i));
  };

  const updateDeficiency = (i, k, v) => {
    const arr = [...(form.deficiencies || [])];
    arr[i] = { ...arr[i], [k]: v };
    set("deficiencies", arr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { ...form, cost: form.cost ? Number(form.cost) : undefined };
    if (inspection?.id) {
      await hosys.entities.ElectricalInspection.update(inspection.id, data);
    } else {
      await hosys.entities.ElectricalInspection.create(data);
      // Also create deficiency records
      for (const d of (data.deficiencies || [])) {
        if (d.description) {
          await hosys.entities.ElectricalDeficiency.create({
            venue_id: venueId,
            installation_id: installation?.id,
            installation_name: installation?.name,
            description: d.description,
            severity: d.severity,
            code: d.code,
            detection_date: data.inspection_date,
            status: d.resolved ? "resolved" : "open",
          });
        }
      }
    }
    // Update installation's last/next inspection dates
    if (installation?.id) {
      await hosys.entities.ElectricalInstallation.update(installation.id, {
        last_inspection_date: data.inspection_date,
        next_inspection_date: data.next_inspection_date || undefined,
        status: data.result === "favorable" ? "ok"
          : data.result === "favorable_with_conditions" ? "deficiency_minor"
          : data.result === "unfavorable" ? "deficiency_major"
          : installation.status,
      });
    }
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{inspection?.id ? "Editar inspecció" : "Registrar inspecció"} — {form.installation_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Tipus d'inspecció</Label>
              <Select value={form.inspection_type} onValueChange={v => set("inspection_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data inspecció *</Label>
              <Input type="date" value={form.inspection_date} onChange={e => set("inspection_date", e.target.value)} required />
            </div>
            <div>
              <Label>Pròxima inspecció</Label>
              <Input type="date" value={form.next_inspection_date} onChange={e => set("next_inspection_date", e.target.value)} />
            </div>
            <div>
              <Label>Entitat / empresa inspectora</Label>
              <Input value={form.inspector_entity} onChange={e => set("inspector_entity", e.target.value)} placeholder="OCA / empresa autoritzada" />
            </div>
            <div>
              <Label>Tècnic inspector</Label>
              <Input value={form.inspector_name} onChange={e => set("inspector_name", e.target.value)} placeholder="Nom del tècnic" />
            </div>
            <div>
              <Label>Resultat</Label>
              <Select value={form.result} onValueChange={v => set("result", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{RESULTS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cost (€)</Label>
              <Input type="number" step="0.01" value={form.cost} onChange={e => set("cost", e.target.value)} placeholder="0.00" />
            </div>
            <div className="sm:col-span-2">
              <Label>Observacions</Label>
              <Textarea value={form.observations} onChange={e => set("observations", e.target.value)} rows={2} />
            </div>
          </div>

          {/* Deficiències */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Deficiències detectades</Label>
              <Button type="button" size="sm" variant="outline" onClick={addDeficiency} className="gap-1">
                <Plus className="w-3 h-3" /> Afegir
              </Button>
            </div>
            <div className="space-y-2">
              {(form.deficiencies || []).map((d, i) => (
                <div key={`day-${i}`} className="grid grid-cols-12 gap-2 items-start bg-gray-50 p-3 rounded-lg">
                  <div className="col-span-2">
                    <Input value={d.code} onChange={e => updateDeficiency(i, "code", e.target.value)} placeholder="Codi" className="text-xs" />
                  </div>
                  <div className="col-span-6">
                    <Input value={d.description} onChange={e => updateDeficiency(i, "description", e.target.value)} placeholder="Descripció deficiència" className="text-xs" />
                  </div>
                  <div className="col-span-3">
                    <Select value={d.severity} onValueChange={v => updateDeficiency(i, "severity", v)}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lleu">Lleu</SelectItem>
                        <SelectItem value="greu">Greu</SelectItem>
                        <SelectItem value="molt_greu">Molt greu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeDeficiency(i)} className="h-9 w-9 text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel·lar</Button>
            <Button type="submit" disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
              {saving ? "Desant..." : "Desar inspecció"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}